import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, MessageSquare, ArrowUpCircle, Clock, Shield, Scale, ChevronDown, ChevronUp } from 'lucide-react';
import CriseCartaoDetalhePage from './CriseCartaoDetalhePage';
import { colors, API, fmt, faseColor, riscoColor, escalacaoLabel, statusAcordoColor, authHeaders } from '../utils/theme';
import {
  PageHeader, SearchBar, Select, Table, Th, Td, TrHover, Badge,
  Modal, FormField, Input, TextArea, Button, LoadingSpinner, EmptyState,
} from '../components/UI';

const CANAIS = ['Titular', 'SAC', 'Ouvidoria', 'Bacen', 'Consumidor.gov', 'Procon', 'Reclame Aqui'];
const RESULTADOS_NEG = ['EM_ANDAMENTO', 'ACEITO', 'RECUSADO', 'CONTRAPROPOSTA'];

const LABELS_CRISE = {
  JUROS_ENCARGOS_IOF: 'Juros/Encargos/IOF',
  TAXA_COMPANHIA: 'Taxa/Companhia',
  COMPRA_PONTOS: 'Compra de Pontos',
  PARCELA_FINANCIADA: 'Parcela Financiada',
};
const CORES_CRISE = {
  JUROS_ENCARGOS_IOF: '#ef4444',
  TAXA_COMPANHIA: '#eab308',
  COMPRA_PONTOS: '#3b82f6',
  PARCELA_FINANCIADA: '#f97316',
};

function CategoriaCriseTag({ categoria }) {
  if (!categoria || !LABELS_CRISE[categoria]) return <span style={{ color: '#6b7280', fontSize: 11 }}>—</span>;
  return (
    <span style={{
      background: CORES_CRISE[categoria] + '22',
      color: CORES_CRISE[categoria],
      border: `1px solid ${CORES_CRISE[categoria]}44`,
      borderRadius: 4, padding: '2px 7px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {LABELS_CRISE[categoria]}
    </span>
  );
}

// === Stat Card Component ===
function StatCard({ label, value, subValue, color, icon: Icon }) {
  return (
    <div style={{
      background: colors.card, borderRadius: 12, padding: 16, border: `1px solid ${colors.border}`,
      flex: '1 1 180px', minWidth: 160,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 4 }}>{label}</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: color || colors.text }}>{value}</div>
          {subValue && <div style={{ fontSize: 12, color: colors.textDim, marginTop: 2 }}>{subValue}</div>}
        </div>
        {Icon && <Icon size={20} color={color || colors.textDim} />}
      </div>
    </div>
  );
}

// === Timeline Component ===
function TimelineView({ events }) {
  if (!events || events.length === 0) return <EmptyState message="Nenhum evento registrado" icon={Clock} />;
  return (
    <div style={{ position: 'relative', paddingLeft: 24 }}>
      <div style={{ position: 'absolute', left: 8, top: 0, bottom: 0, width: 2, background: colors.border }} />
      {events.map((ev, i) => {
        const isNeg = ev.tipo === 'NEGOCIACAO';
        const dotColor = isNeg ? colors.primary : colors.warning;
        return (
          <div key={`${ev.tipo}-${ev.id}`} style={{ position: 'relative', marginBottom: 16, paddingLeft: 20 }}>
            <div style={{
              position: 'absolute', left: -4, top: 6, width: 12, height: 12, borderRadius: '50%',
              background: dotColor, border: `2px solid ${colors.card}`,
            }} />
            <div style={{ background: colors.bg, borderRadius: 8, padding: 12, border: `1px solid ${colors.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <Badge text={isNeg ? 'Negociacao' : 'Escalacao'} color={dotColor} />
                <span style={{ fontSize: 11, color: colors.textDim }}>{ev.data}</span>
              </div>
              {isNeg ? (
                <>
                  <div style={{ fontSize: 13, color: colors.text }}>
                    <strong>Canal:</strong> {ev.canal} | <strong>Protocolo:</strong> {ev.protocolo || '-'}
                  </div>
                  <div style={{ fontSize: 13, color: colors.text, marginTop: 4 }}>
                    <strong>Valor proposto:</strong> {fmt(ev.valor_proposto)} ({ev.desconto_pct}% desc.) | <strong>Parcelas:</strong> {ev.parcelas_propostas || '-'}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Badge text={ev.resultado} color={
                      ev.resultado === 'ACEITO' ? colors.success : ev.resultado === 'RECUSADO' ? colors.danger : colors.warning
                    } />
                  </div>
                  {ev.proxima_acao && <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>Proxima: {ev.proxima_acao} ({ev.data_proxima_acao})</div>}
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13, color: colors.text }}>
                    <strong>De:</strong> {escalacaoLabel(ev.nivel_anterior)} <strong>Para:</strong> {escalacaoLabel(ev.nivel_atual)}
                  </div>
                  <div style={{ fontSize: 13, color: colors.text, marginTop: 4 }}>
                    <strong>Motivo:</strong> {ev.motivo_escalacao || '-'}
                  </div>
                  {ev.protocolo_novo && <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>Protocolo: {ev.protocolo_novo}</div>}
                  {ev.resultado && <div style={{ marginTop: 4 }}><Badge text={ev.resultado} color={colors.info} /></div>}
                </>
              )}
              {ev.observacoes && <div style={{ fontSize: 12, color: colors.textDim, marginTop: 6, fontStyle: 'italic' }}>{ev.observacoes}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function CriseGerenciamentoPage() {
  const [dashboard, setDashboard] = useState(null);
  const [cartoes, setCartoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({ fase: '', risco: '', nivel_escalacao: '', search: '' });
  const [detalhCartaoId, setDetalhCartaoId] = useState(null);

  // Modals
  const [negModal, setNegModal] = useState(null); // card object
  const [escModal, setEscModal] = useState(null); // card object
  const [timelineModal, setTimelineModal] = useState(null); // card id
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Transactions section
  const [lancamentosOpen, setLancamentosOpen] = useState(false);
  const [lancamentos, setLancamentos] = useState([]);
  const [lancamentosLoading, setLancamentosLoading] = useState(false);

  // Pagamentos section
  const [pagamentosOpen, setPagamentosOpen] = useState(false);
  const [pagamentos, setPagamentos] = useState([]);
  const [pagamentosLoading, setPagamentosLoading] = useState(false);

  // Negotiation form
  const [negForm, setNegForm] = useState({
    data_negociacao: new Date().toISOString().split('T')[0], canal: 'SAC', protocolo: '',
    contato_nome: '', valor_proposto: '', desconto_pct: '', parcelas_propostas: '',
    contraproposta_valor: '', contraproposta_parcelas: '',
    resultado: 'EM_ANDAMENTO', fundamentacao_legal: '', proxima_acao: '', data_proxima_acao: '',
    gravacao_protocolo: '', observacoes: '',
  });

  // Escalation form
  const [escForm, setEscForm] = useState({
    data_escalacao: new Date().toISOString().split('T')[0], nivel_atual: '',
    motivo_escalacao: '', protocolo_anterior: '', protocolo_novo: '',
    prazo_resposta: '', observacoes: '',
  });

  const loadDashboard = useCallback(() => {
    fetch(`${API}/cartoes/crise/dashboard`, { headers: authHeaders() }).then(r => r.json()).then(setDashboard).catch(console.error);
  }, []);

  const loadCartoes = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(filtros).forEach(([k, v]) => { if (v) params.set(k, v); });
    // Always filter for crisis-relevant cards
    fetch(`${API}/cartoes?${params}`, { headers: authHeaders() }).then(r => r.json()).then(data => {
      // Show all if no filter, but sort by risk
      setCartoes(data);
    }).catch(console.error).finally(() => setLoading(false));
  }, [filtros]);

  useEffect(() => { loadDashboard(); loadCartoes(); }, [loadDashboard, loadCartoes]);

  const openNegociacao = (card) => {
    setNegModal(card);
    setNegForm(f => ({
      ...f,
      data_negociacao: new Date().toISOString().split('T')[0],
      canal: card.nivel_escalacao >= 5 ? 'Procon' : card.nivel_escalacao >= 4 ? 'Consumidor.gov' :
        card.nivel_escalacao >= 3 ? 'Bacen' : card.nivel_escalacao >= 2 ? 'Ouvidoria' : 'SAC',
      protocolo: '', contato_nome: '', valor_proposto: '', desconto_pct: '', parcelas_propostas: '',
      resultado: 'EM_ANDAMENTO', fundamentacao_legal: '', proxima_acao: '', data_proxima_acao: '', observacoes: '',
    }));
  };

  const openEscalacao = (card) => {
    setEscModal(card);
    const nextLevel = Math.min((card.nivel_escalacao || 0) + 1, 6);
    setEscForm({
      data_escalacao: new Date().toISOString().split('T')[0], nivel_atual: String(nextLevel),
      motivo_escalacao: '', protocolo_anterior: '', protocolo_novo: '', prazo_resposta: '', observacoes: '',
    });
  };

  const openTimeline = async (cardId) => {
    setTimelineModal(cardId);
    setTimelineLoading(true);
    try {
      const data = await fetch(`${API}/cartoes/crise/timeline/${cardId}`, { headers: authHeaders() }).then(r => r.json());
      setTimeline(data);
    } catch (e) { console.error(e); setTimeline([]); }
    setTimelineLoading(false);
  };

  const saveNegociacao = async () => {
    if (!negModal) return;
    setSaving(true);
    try {
      const resp = await fetch(`${API}/cartoes/${negModal.id}/negociacoes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(negForm),
      });
      if (!resp.ok) { const err = await resp.json(); throw new Error(err.error); }
      const result = await resp.json();
      setNegModal(null);
      if (result.parcelas_geradas > 0) {
        alert(`✅ Negociação registrada!\n${result.parcelas_geradas} parcela(s) gerada(s) em Contas a Pagar.`);
      }
      loadDashboard(); loadCartoes();
    } catch (e) { alert('Erro: ' + e.message); }
    setSaving(false);
  };

  const saveEscalacao = async () => {
    if (!escModal) return;
    setSaving(true);
    try {
      const resp = await fetch(`${API}/cartoes/${escModal.id}/escalacoes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(escForm),
      });
      if (!resp.ok) { const err = await resp.json(); throw new Error(err.error); }
      setEscModal(null);
      loadDashboard(); loadCartoes();
    } catch (e) { alert('Erro: ' + e.message); }
    setSaving(false);
  };

  const updNeg = (k, v) => setNegForm(f => ({ ...f, [k]: v }));
  const updEsc = (k, v) => setEscForm(f => ({ ...f, [k]: v }));

  const toggleLancamentos = async () => {
    if (lancamentosOpen) { setLancamentosOpen(false); return; }
    setLancamentosOpen(true);
    if (lancamentos.length > 0) return;
    setLancamentosLoading(true);
    try {
      const r = await fetch(`${API}/transacoes?incluir_negociacao=true&limit=500`);
      const d = await r.json();
      setLancamentos(d.data || []);
    } catch (e) { console.error(e); }
    setLancamentosLoading(false);
  };

  const togglePagamentos = async () => {
    if (pagamentosOpen) { setPagamentosOpen(false); return; }
    setPagamentosOpen(true);
    setPagamentosLoading(true);
    try {
      const r = await fetch(`${API}/contas-a-pagar?tipo=FATURA_CRISE`);
      const d = await r.json();
      setPagamentos(d.data || []);
    } catch (e) { console.error(e); }
    setPagamentosLoading(false);
  };

  const marcarPagamentoCrise = async (item) => {
    const dataPgto = prompt('Data do pagamento (AAAA-MM-DD):', new Date().toISOString().split('T')[0]);
    if (!dataPgto) return;
    const valorPago = prompt('Valor pago (R$):', String(item.valor));
    if (!valorPago) return;
    try {
      const r = await fetch(`${API}/contas-a-pagar/${item.id}/pagar`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ data_pagamento: dataPgto, valor_pago: parseFloat(valorPago) }),
      });
      if (!r.ok) throw new Error((await r.json()).error);
      // Reload pagamentos
      const r2 = await fetch(`${API}/contas-a-pagar?tipo=FATURA_CRISE`);
      const d2 = await r2.json();
      setPagamentos(d2.data || []);
    } catch (e) { alert('Erro: ' + e.message); }
  };

  if (loading) return <LoadingSpinner />;

  // Render card detail page
  if (detalhCartaoId) {
    return <CriseCartaoDetalhePage cartaoId={detalhCartaoId} onBack={() => setDetalhCartaoId(null)} />;
  }

  const kpis = dashboard?.kpis || {};

  // Filter: gerenciamento_crise = SIM é o critério principal
  const criseCartoes = cartoes.filter(c =>
    c.gerenciamento_crise === 'SIM' || c.dias_atraso > 0 || c.risco !== 'BAIXO' ||
    c.status_acordo === 'EM_NEGOCIACAO' || c.serasa === 'SIM' || c.protesto === 'SIM' ||
    c.nivel_escalacao > 0 || c.judicializado === 'SIM'
  ).sort((a, b) => {
    // Gerenciamento de crise primeiro, depois por risco
    if (a.gerenciamento_crise === 'SIM' && b.gerenciamento_crise !== 'SIM') return -1;
    if (b.gerenciamento_crise === 'SIM' && a.gerenciamento_crise !== 'SIM') return 1;
    const riscoOrder = { 'CRITICO': 0, 'ALTO': 1, 'MEDIO': 2, 'BAIXO': 3 };
    return (riscoOrder[a.risco] || 3) - (riscoOrder[b.risco] || 3) || (b.fatura_atual || 0) - (a.fatura_atual || 0);
  });

  return (
    <div>
      <PageHeader title="Gestao de Crise" subtitle={`${criseCartoes.length} cartoes em situacao de crise`} />

      {/* KPI Cards */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard label="Total em Atraso" value={kpis.total_em_atraso || 0}
          subValue={fmt(kpis.valor_em_atraso)} color={colors.danger} icon={AlertTriangle} />
        <StatCard label="Fase 1 (0-30d)" value={kpis.fase1_count || 0}
          subValue={fmt(kpis.fase1_valor)} color={colors.warning} icon={Clock} />
        <StatCard label="Fase 2 (30-89d)" value={kpis.fase2_count || 0}
          subValue={fmt(kpis.fase2_valor)} color="#f97316" icon={MessageSquare} />
        <StatCard label="Fase 3 (90+d)" value={kpis.fase3_count || 0}
          subValue={fmt(kpis.fase3_valor)} color={colors.dangerDark} icon={Scale} />
        <StatCard label="Em Negociacao" value={kpis.em_negociacao || 0}
          color={colors.info} icon={MessageSquare} />
        <StatCard label="No Serasa" value={kpis.no_serasa || 0}
          subValue={fmt(kpis.valor_serasa)} color={colors.danger} icon={Shield} />
        <StatCard label="Em Ger. Crise" value={kpis.em_gerenciamento_crise || 0}
          subValue={fmt(kpis.valor_gerenciamento_crise)} color={colors.primaryDark} icon={AlertTriangle} />
        <StatCard label="Judicializados" value={kpis.judicializados || 0}
          subValue={fmt(kpis.valor_judicializado)} color={colors.dangerDark} icon={Scale} />
      </div>

      {/* Prazos Proximos */}
      {dashboard?.prazos_proximos?.length > 0 && (
        <div style={{
          background: colors.card, borderRadius: 12, padding: 16, marginBottom: 16,
          border: `1px solid ${colors.warning}40`,
        }}>
          <h3 style={{ color: colors.warning, fontSize: 14, margin: '0 0 8px 0' }}>
            <Clock size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
            Prazos Proximos (7 dias)
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {dashboard.prazos_proximos.map(p => (
              <div key={p.id} style={{
                background: colors.bg, borderRadius: 6, padding: '6px 12px', fontSize: 12,
                border: `1px solid ${colors.border}`,
              }}>
                <strong>{p.nome_cartao}</strong> ({p.titular_nome}) - {p.data_proxima_rodada}
                <span style={{ marginLeft: 8, color: colors.textDim }}>
                  {escalacaoLabel(p.nivel_escalacao)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <SearchBar value={filtros.search} onChange={e => setFiltros(f => ({ ...f, search: e.target.value }))}
        placeholder="Buscar cartao ou titular...">
        <Select value={filtros.fase} onChange={e => setFiltros(f => ({ ...f, fase: e.target.value }))} style={{ width: 'auto', minWidth: 160 }}>
          <option value="">Todas Fases</option>
          <option value="ATRASO &lt;30">Fase 1 (0-30d)</option>
          <option value="ATRASO 30-60">Atraso 30-60</option>
          <option value="ATRASO 60-90">Atraso 60-90</option>
          <option value="ATRASO &gt;90">Fase 3 (90+d)</option>
        </Select>
        <Select value={filtros.risco} onChange={e => setFiltros(f => ({ ...f, risco: e.target.value }))} style={{ width: 'auto', minWidth: 140 }}>
          <option value="">Todos Riscos</option>
          <option value="CRITICO">Critico</option>
          <option value="ALTO">Alto</option>
          <option value="MEDIO">Medio</option>
          <option value="BAIXO">Baixo</option>
        </Select>
      </SearchBar>

      {/* Crisis Table */}
      {criseCartoes.length === 0 ? (
        <EmptyState message="Nenhum cartao em situacao de crise" icon={AlertTriangle} />
      ) : (
        <Table>
          <thead><tr>
            <Th>Cartao</Th>
            <Th>Titular</Th>
            <Th>Banco</Th>
            <Th>Fatura</Th>
            <Th>Atraso</Th>
            <Th>Fase</Th>
            <Th>Risco</Th>
            <Th>Crise</Th>
            <Th>Judicial</Th>
            <Th>Escalacao</Th>
            <Th>Acordo</Th>
            <Th align="center">Acoes</Th>
          </tr></thead>
          <tbody>
            {criseCartoes.map(c => (
              <TrHover key={c.id}>
                <Td style={{ fontWeight: 600, maxWidth: 180 }}>
                  <button onClick={() => setDetalhCartaoId(c.id)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: colors.primary,
                    fontWeight: 700, fontSize: 13, textAlign: 'left', padding: 0, textDecoration: 'underline',
                  }}>
                    {c.nome_cartao}
                  </button>
                </Td>
                <Td>{c.titular_nome}</Td>
                <Td>{c.banco_nome || '-'}</Td>
                <Td style={{ fontWeight: 600, color: colors.danger }}>{fmt(c.fatura_atual)}</Td>
                <Td style={{ color: c.dias_atraso >= 90 ? colors.dangerDark : c.dias_atraso >= 30 ? colors.warning : colors.text, fontWeight: 600 }}>
                  {c.dias_atraso > 0 ? `${c.dias_atraso}d` : '-'}
                </Td>
                <Td><Badge text={c.fase || 'N/A'} color={faseColor(c.fase)} /></Td>
                <Td><Badge text={c.risco || 'BAIXO'} color={riscoColor(c.risco)} /></Td>
                <Td style={{ textAlign: 'center' }}>
                  {c.gerenciamento_crise === 'SIM' ?
                    <span style={{ color: colors.danger, fontWeight: 700 }}>SIM</span> :
                    <span style={{ color: colors.textDim }}>-</span>}
                </Td>
                <Td style={{ textAlign: 'center' }}>
                  {c.judicializado === 'SIM' ?
                    <span style={{ color: colors.dangerDark, fontWeight: 700 }}>SIM</span> :
                    <span style={{ color: colors.textDim }}>-</span>}
                </Td>
                <Td>
                  <span style={{ fontSize: 12, color: c.nivel_escalacao > 0 ? colors.warning : colors.textDim }}>
                    {escalacaoLabel(c.nivel_escalacao)}
                  </span>
                </Td>
                <Td>
                  <Badge text={(c.status_acordo || 'SEM_ACORDO').replace('_', ' ')}
                    color={statusAcordoColor(c.status_acordo)} />
                </Td>
                <Td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => openNegociacao(c)} title="Negociar"
                      style={{ background: colors.primary, color: '#fff', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: 11 }}>
                      Negociar
                    </button>
                    <button onClick={() => openEscalacao(c)} title="Escalar"
                      style={{ background: colors.warning, color: '#000', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: 11 }}>
                      Escalar
                    </button>
                    <button onClick={() => openTimeline(c.id)} title="Timeline"
                      style={{ background: colors.card, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: 11 }}>
                      Timeline
                    </button>
                  </div>
                </Td>
              </TrHover>
            ))}
          </tbody>
        </Table>
      )}

      {/* Lancamentos de Crise */}
      <div style={{ marginTop: 24, background: colors.card, borderRadius: 12, border: `1px solid ${colors.border}` }}>
        <button
          onClick={toggleLancamentos}
          style={{
            width: '100%', background: 'transparent', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 20px', color: colors.text, fontSize: 14, fontWeight: 600,
          }}
        >
          <span>
            <AlertTriangle size={14} style={{ verticalAlign: -2, marginRight: 8, color: colors.warning }} />
            Lancamentos de Cartoes em Negociacao
            {lancamentos.length > 0 && (
              <span style={{ marginLeft: 8, fontSize: 12, color: colors.textMuted, fontWeight: 400 }}>
                ({lancamentos.length} lancamentos)
              </span>
            )}
          </span>
          {lancamentosOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {lancamentosOpen && (
          <div style={{ borderTop: `1px solid ${colors.border}`, padding: '0 0 8px 0' }}>
            {lancamentosLoading ? (
              <div style={{ padding: 24, textAlign: 'center', color: colors.textMuted }}>Carregando...</div>
            ) : lancamentos.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: colors.textMuted }}>Nenhum lancamento encontrado</div>
            ) : (
              <Table>
                <thead><tr>
                  <Th>Data</Th>
                  <Th>Cartao</Th>
                  <Th>Titular</Th>
                  <Th>Descricao</Th>
                  <Th>Valor</Th>
                  <Th>Categoria</Th>
                </tr></thead>
                <tbody>
                  {lancamentos.map(t => (
                    <TrHover key={t.id}>
                      <Td style={{ fontSize: 12, color: colors.textMuted, whiteSpace: 'nowrap' }}>
                        {t.data_vencimento || t.data_compra || '-'}
                      </Td>
                      <Td style={{ fontSize: 12, maxWidth: 160 }}>{t.nome_cartao || '-'}</Td>
                      <Td style={{ fontSize: 12 }}>{t.titular_nome || '-'}</Td>
                      <Td style={{ fontSize: 12, maxWidth: 240 }}>{t.descricao}</Td>
                      <Td style={{ fontWeight: 600, color: t.valor < 0 ? colors.success : colors.danger }}>
                        {fmt(t.valor)}
                      </Td>
                      <Td><CategoriaCriseTag categoria={t.categoria} /></Td>
                    </TrHover>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        )}
      </div>

      {/* Controle de Pagamentos — Faturas Crise */}
      <div style={{ marginTop: 16, background: colors.card, borderRadius: 12, border: `1px solid ${colors.border}` }}>
        <button
          onClick={togglePagamentos}
          style={{
            width: '100%', background: 'transparent', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 20px', color: colors.text, fontSize: 14, fontWeight: 600,
          }}
        >
          <span>
            <Clock size={14} style={{ verticalAlign: -2, marginRight: 8, color: colors.primary }} />
            Controle de Pagamentos — Faturas em Negociação
            {pagamentos.length > 0 && (
              <span style={{ marginLeft: 8, fontSize: 12, color: colors.textMuted, fontWeight: 400 }}>
                ({pagamentos.length} faturas)
              </span>
            )}
          </span>
          {pagamentosOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {pagamentosOpen && (
          <div style={{ borderTop: `1px solid ${colors.border}`, padding: '0 0 8px 0' }}>
            {pagamentosLoading ? (
              <div style={{ padding: 24, textAlign: 'center', color: colors.textMuted }}>Carregando...</div>
            ) : pagamentos.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: colors.textMuted }}>
                Nenhuma fatura encontrada. Faça upload de um PDF de fatura para um cartão em negociação.
              </div>
            ) : (() => {
              // Calcular fatura mais antiga sem pagar
              const hoje = new Date();
              const pendentes = pagamentos.filter(p => p.status === 'PENDENTE' || p.status === 'ATRASADO');
              const maisAntiga = pendentes.length > 0 ? pendentes.reduce((acc, p) =>
                p.data_vencimento < acc.data_vencimento ? p : acc, pendentes[0]) : null;
              const diasMaisAntiga = maisAntiga
                ? Math.floor((hoje - new Date(maisAntiga.data_vencimento)) / 86400000)
                : 0;

              const totalPendente = pendentes.reduce((s, p) => s + (p.valor || 0), 0);
              const totalPago = pagamentos.filter(p => p.status === 'PAGO').reduce((s, p) => s + (p.valor_pago || p.valor || 0), 0);

              return (
                <>
                  {/* Mini KPIs */}
                  <div style={{ display: 'flex', gap: 12, padding: '12px 16px', flexWrap: 'wrap' }}>
                    <div style={{ background: colors.bg, borderRadius: 8, padding: '8px 14px', border: `1px solid ${colors.border}`, fontSize: 12 }}>
                      <div style={{ color: colors.textMuted, marginBottom: 2 }}>Total Pendente</div>
                      <div style={{ fontWeight: 700, color: colors.danger, fontSize: 16 }}>{fmt(totalPendente)}</div>
                      <div style={{ color: colors.textDim }}>{pendentes.length} fatura(s)</div>
                    </div>
                    <div style={{ background: colors.bg, borderRadius: 8, padding: '8px 14px', border: `1px solid ${colors.border}`, fontSize: 12 }}>
                      <div style={{ color: colors.textMuted, marginBottom: 2 }}>Total Pago</div>
                      <div style={{ fontWeight: 700, color: colors.success, fontSize: 16 }}>{fmt(totalPago)}</div>
                    </div>
                    {maisAntiga && (
                      <div style={{ background: `${colors.danger}11`, borderRadius: 8, padding: '8px 14px', border: `1px solid ${colors.danger}44`, fontSize: 12 }}>
                        <div style={{ color: colors.textMuted, marginBottom: 2 }}>Fatura + Antiga sem Pagar</div>
                        <div style={{ fontWeight: 700, color: colors.danger, fontSize: 16 }}>{diasMaisAntiga} dias</div>
                        <div style={{ color: colors.textDim }}>{maisAntiga.nome_cartao} — {maisAntiga.mes_referencia}</div>
                      </div>
                    )}
                  </div>

                  <Table>
                    <thead><tr>
                      <Th>Mês Ref.</Th>
                      <Th>Cartão</Th>
                      <Th>Titular</Th>
                      <Th>Vencimento</Th>
                      <Th>Valor Fatura</Th>
                      <Th>Lançamentos</Th>
                      <Th>Atraso (dias)</Th>
                      <Th>Status</Th>
                      <Th align="center">Ações</Th>
                    </tr></thead>
                    <tbody>
                      {pagamentos.map(p => {
                        const isPendente = p.status === 'PENDENTE' || p.status === 'ATRASADO';
                        const atraso = isPendente && p.data_vencimento
                          ? Math.max(0, Math.floor((hoje - new Date(p.data_vencimento)) / 86400000))
                          : 0;
                        return (
                          <TrHover key={p.id} style={{
                            background: p.status === 'ATRASADO' ? `${colors.danger}08` : undefined,
                          }}>
                            <Td style={{ fontWeight: 600, fontSize: 13 }}>{p.mes_referencia || '—'}</Td>
                            <Td style={{ fontSize: 12 }}>{p.nome_cartao || '—'}</Td>
                            <Td style={{ fontSize: 12 }}>{p.titular_nome || '—'}</Td>
                            <Td style={{ fontSize: 12, whiteSpace: 'nowrap',
                              color: atraso > 0 ? colors.danger : colors.text,
                              fontWeight: atraso > 0 ? 600 : 400 }}>
                              {p.data_vencimento}
                            </Td>
                            <Td style={{ fontWeight: 700, color: isPendente ? colors.danger : colors.textMuted }}>
                              {fmt(p.valor_fatura_total || p.valor)}
                            </Td>
                            <Td style={{ fontSize: 12, color: colors.textMuted }}>
                              {p.total_lancamentos || '—'}
                            </Td>
                            <Td style={{ fontWeight: 700, color: atraso > 90 ? colors.dangerDark : atraso > 30 ? colors.danger : atraso > 0 ? colors.warning : colors.textDim }}>
                              {atraso > 0 ? `${atraso}d` : p.status === 'PAGO' ? '—' : '0d'}
                            </Td>
                            <Td>
                              <span style={{
                                fontSize: 11, fontWeight: 600, borderRadius: 4, padding: '2px 8px',
                                background: p.status === 'PAGO' ? `${colors.success}22` : p.status === 'ATRASADO' ? `${colors.danger}22` : `${colors.warning}22`,
                                color: p.status === 'PAGO' ? colors.success : p.status === 'ATRASADO' ? colors.danger : colors.warning,
                                border: `1px solid ${p.status === 'PAGO' ? colors.success : p.status === 'ATRASADO' ? colors.danger : colors.warning}44`,
                              }}>
                                {p.status}
                              </span>
                            </Td>
                            <Td>
                              {isPendente && (
                                <button onClick={() => marcarPagamentoCrise(p)}
                                  style={{ background: colors.success, color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                                  Pagar
                                </button>
                              )}
                            </Td>
                          </TrHover>
                        );
                      })}
                    </tbody>
                  </Table>
                </>
              );
            })()}
          </div>
        )}
      </div>

      {/* Por Banco Summary */}
      {dashboard?.por_banco?.length > 0 && (
        <div style={{ background: colors.card, borderRadius: 12, padding: 16, marginTop: 24, border: `1px solid ${colors.border}` }}>
          <h3 style={{ color: colors.text, fontSize: 14, margin: '0 0 12px 0' }}>Cartoes em Crise por Banco</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {dashboard.por_banco.map(b => (
              <div key={b.banco} style={{
                background: colors.bg, borderRadius: 8, padding: '8px 14px', fontSize: 12,
                border: `1px solid ${colors.border}`, minWidth: 140,
              }}>
                <div style={{ fontWeight: 600, color: colors.text }}>{b.banco}</div>
                <div style={{ color: colors.textMuted }}>{b.qtd} cartoes | {b.em_atraso} em atraso</div>
                <div style={{ color: colors.danger, fontWeight: 600 }}>{fmt(b.total)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* === MODAL: Negociacao === */}
      <Modal isOpen={!!negModal} onClose={() => setNegModal(null)}
        title={`Negociacao - ${negModal?.nome_cartao || ''}`} width={750}>
        {negModal && (
          <>
            {/* Card info header */}
            <div style={{
              background: colors.bg, borderRadius: 8, padding: 12, marginBottom: 16,
              border: `1px solid ${colors.border}`, display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13,
            }}>
              <div><strong>Titular:</strong> {negModal.titular_nome}</div>
              <div><strong>Banco:</strong> {negModal.banco_nome}</div>
              <div><strong>Fatura:</strong> <span style={{ color: colors.danger, fontWeight: 600 }}>{fmt(negModal.fatura_atual)}</span></div>
              <div><strong>Atraso:</strong> {negModal.dias_atraso}d</div>
              <div><strong>Rodada atual:</strong> {negModal.rodada_negociacao || 0}</div>
              <div><strong>Escalacao:</strong> {escalacaoLabel(negModal.nivel_escalacao)}</div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <FormField label="Data">
                <Input type="date" value={negForm.data_negociacao} onChange={e => updNeg('data_negociacao', e.target.value)} />
              </FormField>
              <FormField label="Canal">
                <Select value={negForm.canal} onChange={e => updNeg('canal', e.target.value)}>
                  {CANAIS.map(c => <option key={c} value={c}>{c}</option>)}
                </Select>
              </FormField>
              <FormField label="Protocolo">
                <Input value={negForm.protocolo} onChange={e => updNeg('protocolo', e.target.value)} placeholder="Numero do protocolo" />
              </FormField>
              <FormField label="Nome do Contato">
                <Input value={negForm.contato_nome} onChange={e => updNeg('contato_nome', e.target.value)} placeholder="Atendente" />
              </FormField>
              <FormField label="Valor Proposto (R$)">
                <Input type="number" value={negForm.valor_proposto} onChange={e => updNeg('valor_proposto', e.target.value)} placeholder="0.00" />
              </FormField>
              <FormField label="Desconto (%)">
                <Input type="number" step="0.1" value={negForm.desconto_pct} onChange={e => updNeg('desconto_pct', e.target.value)} placeholder="0.0" />
              </FormField>
              <FormField label="Parcelas Propostas">
                <Input type="number" value={negForm.parcelas_propostas} onChange={e => updNeg('parcelas_propostas', e.target.value)} placeholder="0" />
              </FormField>
              <FormField label="Resultado">
                <Select value={negForm.resultado} onChange={e => updNeg('resultado', e.target.value)}>
                  {RESULTADOS_NEG.map(r => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
                </Select>
              </FormField>
              <FormField label="Proxima Acao">
                <Input value={negForm.proxima_acao} onChange={e => updNeg('proxima_acao', e.target.value)} placeholder="Ex: Ligar ouvidoria" />
              </FormField>
              <FormField label="Data Proxima Acao">
                <Input type="date" value={negForm.data_proxima_acao} onChange={e => updNeg('data_proxima_acao', e.target.value)} />
              </FormField>
            </div>
            <FormField label="Fundamentacao Legal">
              <TextArea value={negForm.fundamentacao_legal} onChange={e => updNeg('fundamentacao_legal', e.target.value)}
                placeholder="Art. 6, VIII CDC; Res. BCB 4549..." />
            </FormField>
            <FormField label="Observacoes">
              <TextArea value={negForm.observacoes} onChange={e => updNeg('observacoes', e.target.value)} placeholder="Detalhes da negociacao..." />
            </FormField>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <Button variant="outline" color={colors.textMuted} onClick={() => setNegModal(null)}>Cancelar</Button>
              <Button onClick={saveNegociacao} disabled={saving}>{saving ? 'Salvando...' : 'Registrar Negociacao'}</Button>
            </div>
          </>
        )}
      </Modal>

      {/* === MODAL: Escalacao === */}
      <Modal isOpen={!!escModal} onClose={() => setEscModal(null)}
        title={`Escalar - ${escModal?.nome_cartao || ''}`} width={600}>
        {escModal && (
          <>
            <div style={{
              background: colors.bg, borderRadius: 8, padding: 12, marginBottom: 16,
              border: `1px solid ${colors.border}`, fontSize: 13,
            }}>
              <div style={{ marginBottom: 8 }}>
                <strong>Nivel atual:</strong>{' '}
                <Badge text={escalacaoLabel(escModal.nivel_escalacao)} color={colors.warning} />
              </div>
              <div style={{ color: colors.textMuted, fontSize: 12 }}>
                Hierarquia: SAC {'>'} Ouvidoria {'>'} Bacen {'>'} Consumidor.gov {'>'} Procon {'>'} Reclame Aqui
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <FormField label="Data da Escalacao">
                <Input type="date" value={escForm.data_escalacao} onChange={e => updEsc('data_escalacao', e.target.value)} />
              </FormField>
              <FormField label="Escalar Para">
                <Select value={escForm.nivel_atual} onChange={e => updEsc('nivel_atual', e.target.value)}>
                  {CANAIS.map((c, i) => {
                    const nivel = i + 1;
                    const disabled = nivel <= (escModal.nivel_escalacao || 0);
                    return <option key={c} value={nivel} disabled={disabled}>{nivel} - {c}{disabled ? ' (ja atingido)' : ''}</option>;
                  })}
                </Select>
              </FormField>
              <FormField label="Protocolo Anterior">
                <Input value={escForm.protocolo_anterior} onChange={e => updEsc('protocolo_anterior', e.target.value)} placeholder="Protocolo do nivel anterior" />
              </FormField>
              <FormField label="Novo Protocolo">
                <Input value={escForm.protocolo_novo} onChange={e => updEsc('protocolo_novo', e.target.value)} placeholder="Protocolo deste nivel" />
              </FormField>
              <FormField label="Prazo de Resposta">
                <Input value={escForm.prazo_resposta} onChange={e => updEsc('prazo_resposta', e.target.value)} placeholder="Ex: 10 dias uteis" />
              </FormField>
            </div>
            <FormField label="Motivo da Escalacao">
              <TextArea value={escForm.motivo_escalacao} onChange={e => updEsc('motivo_escalacao', e.target.value)}
                placeholder="Ex: SAC nao resolveu, prazo expirado..." />
            </FormField>
            <FormField label="Observacoes">
              <TextArea value={escForm.observacoes} onChange={e => updEsc('observacoes', e.target.value)} />
            </FormField>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <Button variant="outline" color={colors.textMuted} onClick={() => setEscModal(null)}>Cancelar</Button>
              <Button onClick={saveEscalacao} disabled={saving} style={{ background: colors.warning, color: '#000' }}>
                {saving ? 'Salvando...' : 'Registrar Escalacao'}
              </Button>
            </div>
          </>
        )}
      </Modal>

      {/* === MODAL: Timeline === */}
      <Modal isOpen={!!timelineModal} onClose={() => setTimelineModal(null)}
        title="Timeline de Eventos" width={650}>
        {timelineLoading ? <LoadingSpinner /> : <TimelineView events={timeline} />}
      </Modal>
    </div>
  );
}
